import React, { useState, useMemo, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { ARTWORKS, getMeta, FAMILY_ARCHIVE } from "../data/site";
import { useCurrency } from "../contexts/CurrencyContext";
import { trackRecentlyViewed } from "../components/RecentlyViewed";
import WaitlistCTA from "../components/WaitlistCTA";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function statusLabel(status) {
  switch (status) {
    case "sold":
      return { text: "Sold", className: "bg-[hsl(var(--ink-soft))] text-background" };
    case "private-collection":
      return {
        text: "Private Collection",
        className: "bg-foreground text-background",
      };
    case "on-loan":
      return {
        text: "On Loan — Reserve",
        className: "bg-foreground text-background",
      };
    case "available":
    default:
      return {
        text: "Available — Inquire",
        className: "bg-[hsl(var(--accent))] text-background",
      };
  }
}

// Map an artwork id to a backend print id when a print exists.
const PRINT_ID_FOR_ARTWORK = {
  "donkey-animals": "donkey",
  "sunflower-between-us": "sunflower-between-us",
  "sunflower-field": "sunflower-field",
  "put-on-the-red-dress": "put-on-the-red-dress",
  "pink-cowboy": "pink-cowboy",
  "when-theres-smoke": "when-theres-smoke",
  "she-so-pretty": "she-so-pretty",
  "chocolate-honey": "chocolate-honey",
};

export default function ArtworkDetail() {
  const { slug, artworkId } = useParams();
  const navigate = useNavigate();
  const { format: fmt, code: currencyCode } = useCurrency();
  const series = ARTWORKS[slug];
  const piece = series?.items.find((a) => a.id === artworkId);
  const meta = piece ? getMeta(piece.id) : null;
  const others = useMemo(() => {
    const siblings = (series?.items || []).filter((a) => a.id !== artworkId);
    if (siblings.length >= 3) return siblings.slice(0, 3);
    // Fallback: fill with a curated pull from other series so every page has ≥3 suggestions.
    const seen = new Set([artworkId, ...siblings.map((a) => a.id)]);
    const outsiders = [];
    Object.entries(ARTWORKS).forEach(([sSlug, s]) => {
      if (sSlug === slug) return;
      s.items.forEach((a) => {
        if (!seen.has(a.id)) outsiders.push({ ...a, _fromSlug: sSlug });
      });
    });
    // shuffle-ish: rotate deterministically by artworkId char code sum so it's stable but varied
    const seed = artworkId
      .split("")
      .reduce((acc, c) => acc + c.charCodeAt(0), 0);
    outsiders.sort((a, b) => {
      const ha = (a.id.charCodeAt(0) + seed) % 97;
      const hb = (b.id.charCodeAt(0) + seed) % 97;
      return ha - hb;
    });
    return [...siblings, ...outsiders].slice(0, 3);
  }, [series, artworkId, slug]);

  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  // Track recently viewed for the Home strip.
  useEffect(() => {
    if (piece) trackRecentlyViewed(slug, piece.id);
  }, [slug, piece]);

  if (!piece) {
    return (
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-24">
        <Link to="/work" className="label link-underline">
          ← All Work
        </Link>
        <h1 className="serif text-5xl mt-6">Piece not found.</h1>
      </div>
    );
  }

  const label = statusLabel(meta.status);
  const inquireBody =
    meta.status === "on-loan"
      ? `Hello — I'd like to reserve "${piece.title}" (${piece.year}, ${piece.size}), currently on loan for exhibition. Please share acquisition details and delivery timing after the show closes.`
      : meta.status === "available"
      ? `Hello — I'm interested in "${piece.title}" (${piece.year}, ${piece.size}). Could you share availability and acquisition details?`
      : `Hello — I'm interested in commissioning a piece in the spirit of "${piece.title}". Could we discuss?`;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name,
        email: form.email,
        subject: `Inquiry: ${piece.title}`,
        message: form.message || inquireBody,
      });
      toast.success("Inquiry sent. The studio will reply within 5–7 days.");
      setSent(true);
    } catch (err) {
      toast.error("Couldn't send. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid={`artwork-detail-${piece.id}`}>
      {/* SEO: Schema.org VisualArtwork JSON-LD */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "VisualArtwork",
            name: piece.title,
            image: piece.image,
            description:
              (meta.story || `${piece.title} by Joseph Graves Jr. — ${piece.medium}, ${piece.size}, ${piece.year}.`).replace(/<[^>]+>/g, ""),
            creator: {
              "@type": "Person",
              name: "Joseph Graves Jr.",
              url: "https://josephgravesjr.com",
              nationality: "American",
              address: {
                "@type": "PostalAddress",
                addressLocality: "Denver",
                addressRegion: "CO",
                addressCountry: "US",
              },
            },
            artMedium: piece.medium,
            artform: "Painting",
            artworkSurface: "Canvas",
            width: piece.size?.split("×")[0]?.trim(),
            height: piece.size?.split("×")[1]?.trim(),
            dateCreated: piece.year,
            genre: series.label,
            url: typeof window !== "undefined" ? window.location.href : undefined,
            ...(meta.status === "available" && meta.price
              ? {
                  offers: {
                    "@type": "Offer",
                    price: meta.price,
                    priceCurrency: "USD",
                    availability: "https://schema.org/InStock",
                    seller: {
                      "@type": "Person",
                      name: "Joseph Graves Jr.",
                    },
                    ...(meta.buy_url ? { url: meta.buy_url } : {}),
                  },
                }
              : meta.status === "sold" || meta.status === "private-collection"
              ? {
                  offers: {
                    "@type": "Offer",
                    availability: "https://schema.org/SoldOut",
                  },
                }
              : {}),
          }),
        }}
      />
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 pt-10 md:pt-16">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 label link-underline"
          data-testid="artwork-back"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Back
        </button>
      </div>

      <div className="max-w-[1400px] mx-auto px-6 md:px-10 pt-8 pb-20 md:pb-28 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16">
        {/* Image */}
        <figure className="md:col-span-7">
          <ArtworkView piece={piece} />
          {piece.video && (
            <div className="mt-4 bg-black overflow-hidden" data-testid="artwork-video">
              <video
                src={piece.video}
                controls
                playsInline
                preload="metadata"
                poster={piece.image}
                className="w-full h-auto max-h-[70vh]"
              >
                Your browser does not support the video tag.
              </video>
              <p className="text-xs text-[hsl(var(--ink-soft))] mt-2">
                Studio process · {piece.title}
              </p>
            </div>
          )}
        </figure>

        {/* Meta + Inquire */}
        <aside className="md:col-span-5 md:sticky md:top-24 self-start">
          <p className="label mb-3">
            <Link to={`/work/${slug}`} className="link-underline">
              {series.label}
            </Link>
          </p>
          <h1 className="serif text-4xl md:text-5xl tracking-tight font-light leading-[1.05]">
            {piece.title}
          </h1>
          {piece.subject && (
            <p className="serif italic text-lg text-[hsl(var(--ink-soft))] mt-2">
              Subject: {piece.subject}
            </p>
          )}

          <dl className="mt-8 border-t border-border divide-y divide-border text-sm">
            <Row k="Year" v={piece.year} />
            <Row k="Medium" v={piece.medium} />
            <Row k="Dimensions" v={piece.size} />
            <Row k="Series" v={series.label} />
            {meta.on_view && (
              <Row
                k="Exhibition"
                v={
                  <span className="text-[hsl(var(--accent))]" data-testid="artwork-on-view">
                    {meta.on_view}
                    {meta.on_view_link && (
                      <>
                        {" · "}
                        <a href={meta.on_view_link} target="_blank" rel="noreferrer" className="underline underline-offset-4 hover:text-foreground" data-testid="artwork-on-view-link">
                          Museum page →
                        </a>
                      </>
                    )}
                  </span>
                }
              />
            )}
            <Row
              k="Status"
              v={
                <span
                  className={`inline-block px-2.5 py-1 text-[0.625rem] uppercase tracking-[0.18em] ${label.className}`}
                  data-testid="artwork-status"
                >
                  {label.text}
                </span>
              }
            />
            {meta.price && (meta.status === "available" || meta.status === "on-loan") && (
              <Row
                k="Price"
                v={
                  <>
                    {fmt(meta.price)}
                    {currencyCode !== "USD" && (
                      <span className="text-xs text-[hsl(var(--ink-soft))] ml-2">
                        (${meta.price.toLocaleString()} USD · charged at
                        checkout)
                      </span>
                    )}
                  </>
                }
              />
            )}
          </dl>

          {meta.story && (
            <p className="mt-8 serif italic text-lg text-[hsl(var(--ink-soft))] leading-relaxed">
              {meta.story}
            </p>
          )}

          {meta.prints_available && (
            <Link
              to="/prints"
              className="mt-8 inline-flex items-center justify-between gap-3 w-full border border-foreground px-6 py-4 text-xs uppercase tracking-[0.18em] hover:bg-foreground hover:text-background transition-colors"
              data-testid="artwork-prints-link"
            >
              <span>Original sold · Signed prints available</span>
              <ArrowUpRight className="h-3.5 w-3.5" />
            </Link>
          )}

          {meta.prints_coming_soon && (
            <div
              className="mt-8 inline-flex items-center justify-between gap-3 w-full border border-border px-6 py-4 text-xs uppercase tracking-[0.18em] text-[hsl(var(--ink-soft))]"
              data-testid="artwork-prints-coming-soon"
            >
              <span>
                {meta.status === "available" ? "Prints coming soon" : "Original sold · Prints coming soon"}
                {meta.print_price ? ` · from $${meta.print_price}` : ""}
              </span>
              <Link
                to="/prints#open-editions"
                className="text-[0.625rem] tracking-[0.2em] opacity-70 hover:opacity-100 hover:text-foreground"
                data-testid="artwork-prints-notify-link"
              >
                Notify me →
              </Link>
            </div>
          )}

          {meta.family_archive && (
            <div className="mt-10 border-t border-border pt-8" data-testid="artwork-family-archive">
              <p className="label mb-3">{FAMILY_ARCHIVE.eyebrow}</p>
              <div className="grid grid-cols-2 gap-3">
                {FAMILY_ARCHIVE.photos.map((ph) => (
                  <figure key={ph.id}>
                    <div className="aspect-[4/5] overflow-hidden bg-[hsl(var(--surface))]">
                      <img src={ph.src} alt={ph.alt} className="w-full h-full object-cover" loading="lazy" />
                    </div>
                    <figcaption className="mt-2 text-xs text-[hsl(var(--ink-soft))] leading-snug">{ph.caption}</figcaption>
                  </figure>
                ))}
              </div>
              <p className="mt-4 text-sm text-[hsl(var(--ink-soft))] leading-relaxed">
                Held in the Graves family collection — not for sale. Commissions of your own family are welcome.
              </p>
              <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2">
                <Link to="/about#family" className="label link-underline" data-testid="artwork-family-story-link">The family story →</Link>
                <Link to="/commissions" className="label link-underline" data-testid="artwork-family-commission-link">Commission a portrait →</Link>
              </div>
            </div>
          )}

          {!meta.family_archive && (meta.status === "sold" ||
            meta.status === "private-collection" ||
            meta.status === "private") && (
            <WaitlistCTA
              seriesSlug={slug}
              seriesLabel={series.label}
              pieceTitle={piece.title}
            />
          )}

          {/* Inquire form */}
          {!meta.family_archive && (
          <div id="inquire" className="mt-10 border-t border-border pt-8 scroll-mt-28">
            {meta.status === "on-loan" && (
              <div className="mb-8 border border-foreground/30 bg-[hsl(var(--surface))] p-5" data-testid="artwork-on-loan-note">
                <p className="label mb-2">On loan · Available to reserve</p>
                <p className="text-sm text-[hsl(var(--ink-soft))] leading-relaxed">{meta.loan_note}</p>
              </div>
            )}
            {piece.id.startsWith("posse-") && (
              <Link
                to="/work/the-posse"
                className="mb-6 inline-flex items-center justify-between gap-3 w-full border border-foreground/30 bg-[hsl(var(--surface))] px-5 py-3 text-xs uppercase tracking-[0.18em] hover:bg-foreground hover:text-background transition-colors group"
                data-testid="posse-bundle-pill"
              >
                <span>
                  Part of The Posse · buy the crew for{" "}
                  <span className="serif italic normal-case tracking-normal text-sm">
                    {fmt(12000)}
                  </span>
                </span>
                <ArrowUpRight className="h-3.5 w-3.5" />
              </Link>
            )}
            {meta.buy_url && meta.status === "available" && (
              <>
                <a
                  href={meta.buy_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mb-2 inline-flex items-center justify-between gap-3 w-full bg-foreground text-background px-6 py-4 text-xs uppercase tracking-[0.18em] hover:opacity-90"
                  data-testid="artwork-buy-now"
                >
                  <span>Buy Now · Secure checkout via Square</span>
                  <ArrowUpRight className="h-3.5 w-3.5" />
                </a>
                <p
                  className="mb-8 text-xs text-[hsl(var(--ink-soft))]"
                  data-testid="artwork-installments-note"
                >
                  Afterpay & payment plans available at checkout · Ships
                  insured from the studio
                </p>
              </>
            )}
            {sent ? (
              <div data-testid="artwork-inquire-thanks">
                <p className="label mb-2">Sent</p>
                <p className="serif text-2xl">
                  Inquiry received. Joseph will be in touch.
                </p>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="artwork-inquire-form">
                <p className="label mb-4">{meta.status === "on-loan" ? "Reserve this piece" : "Inquire about this piece"}</p>
                <div className="space-y-4">
                  <input
                    required
                    placeholder="Your name"
                    value={form.name}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, name: e.target.value }))
                    }
                    className="w-full bg-transparent border-b border-border py-2.5 outline-none focus:border-foreground"
                    data-testid="artwork-inquire-name"
                  />
                  <input
                    required
                    type="email"
                    placeholder="Email"
                    value={form.email}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, email: e.target.value }))
                    }
                    className="w-full bg-transparent border-b border-border py-2.5 outline-none focus:border-foreground"
                    data-testid="artwork-inquire-email"
                  />
                  <textarea
                    rows={4}
                    placeholder={inquireBody}
                    value={form.message}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, message: e.target.value }))
                    }
                    className="w-full bg-transparent border border-border p-4 outline-none focus:border-foreground resize-none"
                    data-testid="artwork-inquire-message"
                  />
                </div>
                <button
                  disabled={busy}
                  className="mt-5 inline-flex items-center gap-3 bg-foreground text-background px-7 py-3.5 text-xs uppercase tracking-[0.18em] hover:opacity-90 disabled:opacity-50"
                  data-testid="artwork-inquire-submit"
                >
                  {busy ? "Sending…" : meta.status === "on-loan" ? "Reserve · Send Request" : "Send Inquiry"}
                  <ArrowUpRight className="h-3.5 w-3.5" />
                </button>
              </form>
            )}
          </div>
          )}
        </aside>
      </div>

      {/* You may also like */}
      {others.length > 0 && (
        <section className="border-t border-border bg-[hsl(var(--surface))]">
          <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-16 md:py-24">
            <p className="label mb-3">You may also like</p>
            <h2 className="serif text-3xl md:text-4xl tracking-tight mb-10">
              More from the studio
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-10">
              {others.map((a) => {
                const linkSlug = a._fromSlug || slug;
                const otherMeta = getMeta(a.id);
                return (
                  <Link
                    key={a.id}
                    to={`/work/${linkSlug}/${a.id}`}
                    className="art-card group"
                    data-testid={`more-${a.id}`}
                  >
                    <div className="aspect-[4/5] overflow-hidden bg-background">
                      <img
                        src={a.image}
                        alt={a.title}
                        className="art-img w-full h-full object-cover"
                      />
                    </div>
                    <div className="mt-3 flex items-baseline justify-between gap-4">
                      <span className="serif italic text-lg">{a.title}</span>
                      <span className="label">{a.year}</span>
                    </div>
                    {otherMeta && otherMeta.status === "available" && otherMeta.price && (
                      <div className="mt-1 flex items-center justify-between gap-4 text-xs">
                        <span className="text-[hsl(var(--ink-soft))]">
                          {ARTWORKS[linkSlug]?.label}
                        </span>
                        <span className="label">{fmt(otherMeta.price)}</span>
                      </div>
                    )}
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="grid grid-cols-3 py-3">
      <dt className="label col-span-1">{k}</dt>
      <dd className="col-span-2 text-foreground">{v}</dd>
    </div>
  );
}

/**
 * Artwork image with an optional "See it in a room" toggle.
 * Probes the backend for a generated room preview at
 * /api/room-previews/{artwork_id}.png — shows the tab only if it exists.
 */
function ArtworkView({ piece }) {
  const [tab, setTab] = React.useState("art");
  const [hasRoom, setHasRoom] = React.useState(false);
  const roomUrl = `${process.env.REACT_APP_BACKEND_URL}/api/room-previews/${piece.id}.png`;

  React.useEffect(() => {
    let cancelled = false;
    fetch(roomUrl, { method: "HEAD" })
      .then((r) => {
        if (!cancelled && r.ok) setHasRoom(true);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [roomUrl]);

  return (
    <div>
      {hasRoom && (
        <div
          className="inline-flex items-center gap-0.5 border border-border bg-background p-1 mb-4"
          data-testid="artwork-view-toggle"
        >
          {[
            ["art", "The painting"],
            ["room", "In a room"],
          ].map(([v, label]) => (
            <button
              key={v}
              type="button"
              onClick={() => setTab(v)}
              className={`px-4 py-2 text-[0.65rem] uppercase tracking-[0.18em] transition-colors ${
                tab === v
                  ? "bg-foreground text-background"
                  : "text-[hsl(var(--ink-soft))]"
              }`}
              data-testid={`artwork-view-${v}`}
            >
              {label}
            </button>
          ))}
        </div>
      )}
      <div className="bg-[hsl(var(--surface))] overflow-hidden">
        {tab === "room" && hasRoom ? (
          <img
            src={roomUrl}
            alt={`${piece.title} — shown in a curated room`}
            className="w-full h-auto object-contain max-h-[80vh]"
            data-testid="artwork-room-image"
          />
        ) : (
          <img
            src={piece.image}
            alt={piece.title}
            className="w-full h-auto object-contain max-h-[80vh]"
            data-testid="artwork-image"
          />
        )}
      </div>
      {hasRoom && tab === "room" && (
        <p className="text-xs text-[hsl(var(--ink-soft))] mt-2 italic">
          Illustrative room mockup — the painting is real, the room is a
          designed visualization.
        </p>
      )}
    </div>
  );
}
